import Measurement from '@/classes/Measurement';
import projectsRepository from '@/services/projectsRepository';
import projectVisitsRepository from '@/services/projects/projectsVisitsRepository.ts';
import projectReportsRepository from '@/services/projects/projectsReportsRepository.ts';
import notificationService from '@/services/notificationService';

export default class Project implements IProject {
    public id = '';
    public logs: ILog[] = [];
    public projectNumber = '';
    public name = '';
    public status = 0;
    public declineReason = '';
    public location?: ILocation;
    public type = '';
    public address: IAddress = {
        addressline1: '',
        addressline2: '',
        city: '',
        country: '',
        name: '',
        type: 0,
        zip: '',
    };
    public userId: string | null = null;
    public customerReference: string | null = null;
    public caseNumberRsz: string | null = null;
    public termsOfPayment: number | null = null;
    public measurements?: Measurement[] = [];
    public measuringStates?: IMeasuringState[];
    public requestedExecutionDate?: string = undefined;

    public contacts: IProjectAssignedContact[] = [];
    public visits: IProjectVisit[] = [];
    public reports: IProjectReport[] = [];

    public updating = false;
    public loading = false;
    public siteLabel?: string | null;
    public isOnHold = false;
    public lastOnHoldReason: string | null = null;
    public lastOnHoldCreateDate?: string | null = null;

    constructor(id?: string) {
        if (id) {
            this.InitProject(id);
        }
    }

    public async InitProject(id: string) {
        try {
            this.loading = true;
            await this.LoadProject(id);
        } catch (err) {
            notificationService.showAxiosError(err);
        } finally {
            this.loading = false;
        }
    }

    public async LoadProject(id: string) {
        const result = await projectsRepository.getSingle(id);
        Object.assign(this, result.data);

        if (this.visits === null) {
            this.visits = [];
        }

        if (this.reports === null) {
            this.reports = [];
        }
    }

    public async reload() {
        try {
            await this.LoadProject(this.id);
        } catch (err) {
            notificationService.showAxiosError(err);
        }
    }

    public async duplicateMeasurement(measurementName: string, newName: string) {
        try {
            this.updating = true;
            const dtu: IMeasurementDuplicateDtu = {
                measurementName,
                newName,
            };

            await projectsRepository.duplicateMeasurement(this.id, dtu);
            await this.LoadProject(this.id);
        } catch (err) {
            notificationService.showAxiosError(err);
        } finally {
            this.updating = false;
        }
    }

    public async deleteMeasurement(name: string) {
        try {
            this.updating = true;
            if (!this.measurements) {
                throw Error('Meeting niet teruggevonden');
            }
            const measurement = this.measurements.find((o) => o.name === name);

            if (!measurement) {
                throw Error('Meeting niet teruggevonden');
            }
            await projectsRepository.deleteMeasurement(this.id, measurement);
            await this.LoadProject(this.id);
        } catch (err) {
            notificationService.showAxiosError(err);
        } finally {
            this.updating = false;
        }
    }

    public async updateMeasurement(name: string, updatedObject: Measurement) {
        try {
            this.updating = true;
            await projectsRepository.updateMeasurement(this.id, updatedObject.documentNumber.replace('/', '-').replace('/', '-'), updatedObject, '' + Date.now());
            await this.LoadProject(this.id);
        } catch (err) {
            notificationService.showAxiosError(err);
        } finally {
            this.updating = false;
        }
    }

    public async loadVisits() {
        this.visits = (await projectVisitsRepository.get(this.id)).data;
    }
    public async addOrUpdateVisit(visit: IProjectVisit) {
        try {
            this.updating = true;
            if (!visit.pageId || visit.pageId === null || visit.pageId === '') {
                await projectVisitsRepository.create(this.id, visit);
            } else {
                await projectVisitsRepository.update(this.id, visit);
            }
            this.loadVisits();
            notificationService.saved();
        } catch (err) {
            notificationService.showAxiosError(err);
        } finally {
            this.updating = false;
        }
    }
    public async copyVisit(visit: IProjectVisit) {
        try {
            this.updating = true;
            await projectVisitsRepository.copy(this.id, visit.pageId);

            this.loadVisits();
            notificationService.saved();
        } catch (err) {
            notificationService.showAxiosError(err);
        } finally {
            this.updating = false;
        }
    }
    public async deleteVisit(visit: IProjectVisit) {
        try {
            this.updating = true;
            await projectVisitsRepository.delete(this.id, visit.pageId);
            await this.loadVisits();
        } catch (err) {
            notificationService.showAxiosError(err);
        } finally {
            this.updating = false;
        }
    }

    public async loadReports() {
        this.reports = (await projectReportsRepository.get(this.id)).data;
    }
    public async addOrUpdateReport(report: IProjectReport) {
        try {
            this.updating = true;
            if (!report.pageId || report.pageId === null || report.pageId === '') {
                await projectReportsRepository.create(this.id, report);
            } else {
                await projectReportsRepository.update(this.id, report);
            }
            this.loadReports();
            notificationService.saved();
        } catch (err) {
            notificationService.showAxiosError(err);
        } finally {
            this.updating = false;
        }
    }
    public async deleteReport(report: IProjectReport) {
        try {
            this.updating = true;
            await projectReportsRepository.delete(this.id, report.pageId);
            await this.loadReports();
        } catch (err) {
            notificationService.showAxiosError(err);
        } finally {
            this.updating = false;
        }
    }

    public async save() {
        const result = await projectsRepository.update(this);
        Object.assign(this, result.data);
        notificationService.saved();
        return true;
    }

    public async getFilesAsync(folder: string, file?: string) {
        const request: IFileLookupRequest = {
            documentNumber: this.projectNumber,
            folder,
            file,
        };

        try {
            return (await projectsRepository.getFiles(request)).data;
        } catch {
            notificationService.failedToLoad();
        }
    }
}
